import { type FC, useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { change, Form, type FormErrors, formValueSelector, type InjectedFormProps, reduxForm } from "redux-form";
import { HStack, InfoToggletip, LoadingSpinner } from "widgets";

import { handleTabInForm, simpleDropdownFieldValidation } from "@/app-utils/form.ts";
import { FormInputGroup } from "@/ui-modules/core/form/layout/FormGroup.tsx";
import { FormLabel } from "@/ui-modules/core/form/layout/FormLabel.tsx";
import ReactSelectField, { ReactSelectReduxFormField } from "@/ui-modules/core/redux-form/ReactSelectField.tsx";
import P2ParamsConfigurator from "@/ui-modules/playbooks/editor/P2ParamsConfigurator.tsx";

import { TableParamTypeEnum, UiContextEnum, WorkflowParameterTypeEnum } from "../../../graphql.js";
import {
  type ActivityInstanceExtendedParameterWithActivity,
  fetchP2Workflow,
  type IPlaybookConfigState,
  isInvalidSampleFileParameter,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type PlaybookConfigP2WorkflowInputScreenProps = IPlaybookWizardStepProvidedProps;

enum PlaybookConfigP2WorkflowInputScreenFormNames {
  FORM_NAME = "p2WorkflowInput",
  GENOME_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_GENOME",
  REGION_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_REGION",
  VARIANTS_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_VARIANTS",
  VARIANTS_GENOME_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_VARIANTS_GENOME",
  VARIANTS_TRACK_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_VARIANTS_TRACK",
  VARIANTS_TRACK_GENOME_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_VARIANTS_TRACK_GENOME",
  COORDINATES_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_COORDINATES",
  COORDINATES_GENOME_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_COORDINATES_GENOME",
}

interface ErrorContainer {
  networkError: string | undefined;
  sampleFileError: string | undefined;
}

const PlaybookConfigP2WorkflowInputForm: FC<PlaybookConfigP2WorkflowInputScreenProps & InjectedFormProps<any, any>> = (
  props: PlaybookConfigP2WorkflowInputScreenProps & IPlaybookWizardStepInjectedProps & InjectedFormProps<any, any>,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const configState = useSelector<IAppState, Partial<IPlaybookConfigState>>(
    (state) => state.playbookAdminState.playbookConfigState,
  );
  // we can retrieve the following info from the config state because it was set on the previous screen
  const genomeViewerContextSelected = !!configState.uiContexts?.find((c) => c === UiContextEnum.GenomeViewer);
  const variantsTrackContextSelected = !!configState.uiContexts?.find(
    (c) => c === UiContextEnum.GenomeViewerVariantsTrack,
  );
  const variantsContextSelected = !!configState.uiContexts?.find((c) => c === UiContextEnum.Variants);
  const coordinateListsContextSelected = !!configState.uiContexts?.find((c) => c === UiContextEnum.CoordinateLists);

  const [isSavingPlaybook, setSavingPlaybook] = useState<boolean>(false);
  const [isLoadingData, setLoadingData] = useState<boolean>(true);
  const [errorContainer, setErrorContainer] = useState<ErrorContainer>({
    networkError: undefined,
    sampleFileError: undefined,
  });

  const configurableParameters = configState.p2Params;

  const setConfigurableParameters = (p2Params: ActivityInstanceExtendedParameterWithActivity[]) => {
    dispatch(updatePlaybookConfigState({ p2Params: p2Params }));
  };

  const valueSelector = formValueSelector(PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME);
  const mappedGenome = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD);
  });

  const mappedRegion = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD);
  });

  const mappedCoordinatesGenome = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_GENOME_FIELD);
  });

  const mappedCoordinateList = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_FIELD);
  });

  const mappedVariants = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_FIELD);
  });

  const mappedVariantsGenome = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_GENOME_FIELD);
  });

  const mappedVariantsTrack = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_FIELD);
  });

  const mappedVariantsTrackGenome = useSelector<IAppState, ActivityInstanceExtendedParameterWithActivity>((state) => {
    return valueSelector(state, PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_GENOME_FIELD);
  });

  function handleSubmitData(): Promise<any> {
    const playbookCreationState: Partial<IPlaybookConfigState> = {
      p2ContextParams: {
        genomeParam: mappedGenome,
        regionParam: mappedRegion,
        coordinatesGenomeParam: mappedCoordinatesGenome,
        coordinatesParam: mappedCoordinateList,
        variantsGenomeParam: mappedVariantsGenome,
        variantsParam: mappedVariants,
        variantsTrackParam: mappedVariantsTrack,
        variantsTrackGenomeParam: mappedVariantsTrackGenome,
      },
      p2Params: configurableParameters,
    };

    dispatch(updatePlaybookConfigState(playbookCreationState));

    if (isLastScreen) {
      setErrorContainer((errorContainer) => ({ ...errorContainer, networkError: undefined }));
      setSavingPlaybook(true);
      return dispatch(savePlaybook())
        .then(() => {
          setSavingPlaybook(false);
          return props.proceedWithNextStep();
        })
        .catch((error: Error) => {
          setErrorContainer((errorContainer) => ({ ...errorContainer, networkError: error.message }));
          setSavingPlaybook(false);
        });
    } else {
      return props.proceedWithNextStep();
    }
  }

  useEffect(() => {
    if (errorContainer.networkError) {
      props.updateWizardMessage?.(errorContainer.networkError);
    } else if (errorContainer.sampleFileError) {
      props.updateWizardMessage?.(errorContainer.sampleFileError);
    } else {
      props.updateWizardMessage?.(null);
    }
  }, [errorContainer]);

  useEffect(() => {
    setLoadingData(true);
    dispatch(fetchP2Workflow(configState.workflowId as number))
      .then((_P2Workflow) => {
        setLoadingData(false);
      })
      .catch((error: Error) => {
        setErrorContainer((errorContainer) => ({ ...errorContainer, networkError: error.message }));
        setLoadingData(false);
      });
  }, []);

  const initMappedParameterFormField = useCallback(
    (
      workflowParameters: ActivityInstanceExtendedParameterWithActivity[] | undefined,
      parameter: ActivityInstanceExtendedParameterWithActivity | undefined,
      formName: PlaybookConfigP2WorkflowInputScreenFormNames,
      fieldName: PlaybookConfigP2WorkflowInputScreenFormNames,
    ) => {
      if (parameter) {
        const fullParameter = workflowParameters?.find(
          (c) =>
            c.activity.identifier === parameter?.activity.identifier &&
            c.parameterDefinition.id === parameter?.parameterDefinition.id,
        );
        dispatch(change(formName, fieldName, fullParameter));
      }
    },
    [dispatch],
  );

  // check for mandatory sample file parameters without attribute selection and force its entry via configuration
  useEffect(() => {
    const invalidSampleFileParamNames =
      configurableParameters
        ?.filter((p) => {
          return p.workflowParameter && isInvalidSampleFileParameter(p.workflowParameter, p);
        })
        .map((p) => p.parameterDefinition.label) || [];
    if (invalidSampleFileParamNames.length > 0) {
      setErrorContainer((errorContainer) => ({
        ...errorContainer,
        sampleFileError: `The following attributes require configuration of a default: ${invalidSampleFileParamNames.join(", ")}`,
      }));
    } else {
      setErrorContainer((errorContainer) => ({ ...errorContainer, sampleFileError: undefined }));
    }
  }, [configurableParameters]);

  useEffect(() => {
    const workflowParameters = configState.p2Params;
    const configStateGenomeParam = configState.p2ContextParams?.genomeParam;
    const configStateRegionParam = configState.p2ContextParams?.regionParam;
    const configStateCoordinatesGenomeParam = configState.p2ContextParams?.coordinatesGenomeParam;
    const configStateCoordinatesParam = configState.p2ContextParams?.coordinatesParam;
    const configStateVariantsParam = configState.p2ContextParams?.variantsParam;
    const configStateVariantsGenomeParam = configState.p2ContextParams?.variantsGenomeParam;
    const configStateVariantsTrackParam = configState.p2ContextParams?.variantsTrackParam;
    const configStateVariantsTrackGenomeParam = configState.p2ContextParams?.variantsTrackGenomeParam;

    initMappedParameterFormField(
      workflowParameters,
      configStateGenomeParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateRegionParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateCoordinatesGenomeParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_GENOME_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateCoordinatesParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateVariantsGenomeParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_GENOME_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateVariantsParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateVariantsTrackGenomeParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_GENOME_FIELD,
    );

    initMappedParameterFormField(
      workflowParameters,
      configStateVariantsTrackParam,
      PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
      PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_FIELD,
    );
  }, [configState.p2Params]);

  const isLastScreen: boolean = configState.playbookType === PLAYBOOK_TYPE.PROCESSOR; // only case right now

  // make sure visibility is not switched off for parameters selected for the various mappings
  const lockedVisibilityParams: ActivityInstanceExtendedParameterWithActivity[] = [];
  if (genomeViewerContextSelected) {
    lockedVisibilityParams.push(...[mappedGenome, mappedRegion]);
  }
  if (coordinateListsContextSelected) {
    lockedVisibilityParams.push(...[mappedCoordinateList, mappedCoordinatesGenome]);
  }
  if (variantsContextSelected) {
    lockedVisibilityParams.push(...[mappedVariants, mappedVariantsGenome]);
  }
  if (variantsTrackContextSelected) {
    lockedVisibilityParams.push(...[mappedVariantsTrack, mappedVariantsTrackGenome]);
  }

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description: getPageDescription(
          genomeViewerContextSelected || variantsContextSelected || coordinateListsContextSelected,
        ),
      }}
      wizardFooterData={{
        nextButtonProps: {
          label: isLastScreen ? "Save Playbook" : "Next",
          isDisabled: !!props.wizardErrorMessage || (isLastScreen ? false : false) /* this.props.isLoadingSampleType */,
          isLoading: isLoadingData || (isLastScreen ? isSavingPlaybook : false),
          onPress: props.handleSubmit(handleSubmitData),
        },
      }}
    >
      {isLoadingData ? (
        <LoadingSpinner />
      ) : (
        <>
          {(genomeViewerContextSelected ||
            variantsContextSelected ||
            coordinateListsContextSelected ||
            variantsTrackContextSelected) && (
            // reduxForm handleSubmit()?
            <Form className="form-horizontal" onSubmit={props.handleSubmit(handleSubmitData)}>
              {genomeViewerContextSelected && (
                <FormInputGroup>
                  <FormLabel>{`Settings for Genome Viewer context`}</FormLabel>

                  <div onKeyDownCapture={handleTabInForm}>
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD}
                      entityType={"genome"}
                      message={"This is the parameter that will accept the genome from the Genome Viewer."}
                      options={
                        configurableParameters?.filter(
                          (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.Genome,
                        ) ?? []
                      }
                    />
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD}
                      entityType={"region"}
                      message={"This is the parameter that will accept the genomic region from the Genome Viewer."}
                      options={
                        configurableParameters?.filter(
                          (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.GenomicRegion,
                        ) ?? []
                      }
                    />
                  </div>
                </FormInputGroup>
              )}
              {variantsContextSelected && (
                <FormInputGroup>
                  <FormLabel>{`Settings for Variants context`}</FormLabel>

                  <div onKeyDownCapture={handleTabInForm}>
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_GENOME_FIELD}
                      entityType={"genome"}
                      message={"This is the parameter that will accept the genome related to the variants."}
                      options={
                        configurableParameters?.filter(
                          (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.Genome,
                        ) ?? []
                      }
                    />
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_FIELD}
                      entityType={"variants"}
                      message={"This is the parameter that will accept the variants."}
                      options={
                        configurableParameters?.filter(
                          (p) =>
                            p.staged &&
                            p.parameterDefinition.type === WorkflowParameterTypeEnum.Table &&
                            p.parameterDefinition.tableType === TableParamTypeEnum.Variant,
                        ) ?? []
                      }
                    />
                  </div>
                </FormInputGroup>
              )}
              {variantsTrackContextSelected && (
                <FormInputGroup>
                  <FormLabel>{`Settings for Variants Track context in Genome Viewer`}</FormLabel>

                  <div onKeyDownCapture={handleTabInForm}>
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_GENOME_FIELD}
                      entityType={"genome"}
                      message={"This is the parameter that will accept the genome related to the variants track."}
                      options={
                        configurableParameters?.filter(
                          (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.Genome,
                        ) ?? []
                      }
                    />
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.VARIANTS_TRACK_FIELD}
                      entityType={"variants"}
                      message={"This is the parameter that will accept the variants from the variants track."}
                      options={
                        configurableParameters?.filter(
                          (p) =>
                            p.staged &&
                            p.parameterDefinition.type === WorkflowParameterTypeEnum.Table &&
                            p.parameterDefinition.tableType === TableParamTypeEnum.Variant, // must be different from variant context somehow
                        ) ?? []
                      }
                    />
                  </div>
                </FormInputGroup>
              )}
              {coordinateListsContextSelected && (
                <FormInputGroup>
                  <FormLabel>{`Settings for Coordinate List context`}</FormLabel>

                  <div onKeyDownCapture={handleTabInForm}>
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_GENOME_FIELD}
                      entityType={"genome"}
                      message={"This is the parameter that will accept the genome related to the coordinate lists."}
                      options={
                        configurableParameters?.filter(
                          (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.Genome,
                        ) ?? []
                      }
                    />
                    <ParameterField
                      field={PlaybookConfigP2WorkflowInputScreenFormNames.COORDINATES_FIELD}
                      entityType={"coordinates"}
                      message={"This is the parameter that will accept the coordinate lists."}
                      options={
                        configurableParameters?.filter(
                          (p) =>
                            p.staged &&
                            p.parameterDefinition.type === WorkflowParameterTypeEnum.Table &&
                            p.parameterDefinition.tableType === TableParamTypeEnum.CoordinateList,
                        ) ?? []
                      }
                    />
                  </div>
                </FormInputGroup>
              )}
            </Form>
          )}
          <P2ParamsConfigurator
            lockedVisibilityParams={lockedVisibilityParams}
            p2ParamConfigurations={configurableParameters ?? []}
            setP2ParamConfigurations={setConfigurableParameters}
          />
        </>
      )}
    </PlaybookWizardStep>
  );
};

const getPageDescription = (anySpecificContextSelected: boolean) => {
  return `Configure ${anySpecificContextSelected ? "the input parameters specific for the selected context(s) and" : ""} the workflow parameters exposed by the workflow.`;
};

const getParameterId = (option: ActivityInstanceExtendedParameterWithActivity) => {
  return `${option.activity.identifier}-${option.parameterDefinition.id}`;
};

const validate = (values, _props: PlaybookConfigP2WorkflowInputScreenProps): FormErrors<unknown> => {
  return {};
};

const PlaybookConfigP2WorkflowInputScreen = reduxForm<unknown, PlaybookConfigP2WorkflowInputScreenProps>({
  form: PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
  validate,
})(PlaybookConfigP2WorkflowInputForm);

export default PlaybookConfigP2WorkflowInputScreen;

interface ParameterFieldProps {
  field: string;
  entityType: string;
  message: string;
  options: ActivityInstanceExtendedParameterWithActivity[] | undefined;
}

const ParameterField: FC<ParameterFieldProps> = ({ field, entityType, message, options }) => {
  return (
    <ReactSelectReduxFormField
      key={field}
      name={field}
      label={
        <HStack>
          {`Select a ${entityType} parameter`}
          <InfoToggletip content={message} />
        </HStack>
      }
      horizontalLayout={true}
      isClearable={true}
      isSearchable={true}
      getOptionValue={(option: ActivityInstanceExtendedParameterWithActivity) => getParameterId(option)}
      getOptionLabel={(option: ActivityInstanceExtendedParameterWithActivity) =>
        `${option.parameterDefinition.label} (${option.activity.label})`
      }
      component={ReactSelectField}
      isDisabled={false}
      isLoading={false}
      options={options}
      validate={simpleDropdownFieldValidation}
    />
  );
};
